import { scanEquipmentByCode } from '@/api/rental-equipment';
import { Button, Card, CardContent, Input } from '@/components/ui';
import { useBarcodeScanner } from '@/hooks/pos/use-barcode-scanner';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useBergep, useSuggestedAccessories } from '@/hooks/use-rental-equipment';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useMemo, useState } from 'react';
import { formatCurrency } from '../mock-data';
import type { Equipment } from '../types';
import {
  CATEGORY_LABEL_KEYS,
  CONDITION_LABEL_KEYS,
  EQUIPMENT_STATUS_COLORS,
  EquipmentCategory,
  EquipmentCondition,
  EquipmentStatus,
  STATUS_LABEL_KEYS,
} from '../types';
import { useWizardStore } from '../wizard-store';
import { AccessoryDrawer } from './AccessoryDrawer';
import { AccessorySuggestionPanel } from './AccessorySuggestionPanel';

/** Map API bergep item to local wizard Equipment type */
function toWizardEquipment(item: {
  id: string;
  name: string;
  inventoryCode?: string;
  serialNumber: string;
  category: string;
  brand?: string;
  model?: string;
  status: string;
  condition: string;
  dailyRate: number;
  weeklyRate: number;
  monthlyRate: number;
  hourlyRate?: number;
  depositAmount: number;
}): Equipment {
  const equipment: Equipment = {
    id: item.id,
    name: item.name,
    equipmentCode: item.inventoryCode,
    serialNumber: item.serialNumber,
    category: (item.category as EquipmentCategory) ?? EquipmentCategory.POWER_TOOL,
    brand: item.brand,
    model: item.model,
    status: (item.status as EquipmentStatus) ?? EquipmentStatus.AVAILABLE,
    condition: (item.condition as EquipmentCondition) ?? EquipmentCondition.GOOD,
    dailyRate: item.dailyRate,
    weeklyRate: item.weeklyRate,
    monthlyRate: item.monthlyRate,
    depositAmount: item.depositAmount,
  };

  if (item.hourlyRate != null && item.hourlyRate > 0) {
    equipment.hourlyRate = item.hourlyRate;
  }

  return equipment;
}

export function EquipmentStep() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    isInCart,
    getCartItem,
    partner,
    getCartTotal,
    toggleAccessory,
    getAccessoriesForEquipment,
  } = useWizardStore();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<EquipmentCategory | 'ALL'>('ALL');

  // 105-8a: Server-side debounced search (250ms) — pg_trgm GIN index uses (105-8b)
  const debouncedSearch = useDebouncedValue(search.trim(), 250);

  // 4-lens HOTFIX 2026-04-13 M3: explicit page=1 reset on filter/search change.
  // Was: useBergep took whatever meta.page was; switching category after pagination
  // could land on empty page 3 of a smaller result set.
  const {
    equipment: apiEquipment,
    isLoading,
    error,
    total,
  } = useBergep({
    availableOnly: true,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(categoryFilter !== 'ALL' ? { category: categoryFilter } : {}),
    page: 1, // always start fresh; future micro-story can wire pagination UI
    pageSize: 50, // 105-8a: pagination, NEM virtualization (party mode konszenzus)
  });

  const allEquipment = useMemo(() => apiEquipment.map(toWizardEquipment), [apiEquipment]);

  // Server-side szűrés után csak az AVAILABLE filter marad client-side (defensive).
  const filteredEquipment = useMemo(
    () => allEquipment.filter(eq => eq.status === EquipmentStatus.AVAILABLE),
    [allEquipment]
  );

  const allEquipmentCount = total;

  // 105-8a: Scanner integration (drop-in pattern from SalesPOSPage)
  const handleScan = useCallback(
    async (code: string) => {
      try {
        const equipment = await scanEquipmentByCode(code);
        if (equipment.status !== EquipmentStatus.AVAILABLE) {
          toast({
            title: t('rental.wizard.scanUnavailable'),
            description: equipment.productName,
            variant: 'destructive',
          });
          return;
        }
        // Map scan response to wizard Equipment shape
        const mapped: Equipment = {
          id: equipment.id,
          name: equipment.productName || equipment.equipmentCode,
          equipmentCode: equipment.equipmentCode,
          serialNumber: equipment.serialNumber,
          category: EquipmentCategory.POWER_TOOL, // unknown from scan response
          ...(equipment.productBrand ? { brand: equipment.productBrand } : {}),
          ...(equipment.productModel ? { model: equipment.productModel } : {}),
          status: equipment.status as EquipmentStatus,
          condition: EquipmentCondition.GOOD,
          dailyRate: equipment.dailyRate ?? 0,
          weeklyRate: equipment.weeklyRate ?? 0,
          monthlyRate: equipment.monthlyRate ?? 0,
          depositAmount: equipment.depositAmount ?? 0,
        };
        addToCart(mapped);
        toast({
          title: t('rental.wizard.scanSuccess', { name: mapped.name }),
          description: t(`rental.wizard.scanMatchedField.${equipment.matchedField}`),
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Scan failed';
        toast({
          title: t('rental.wizard.scanError'),
          description: message,
          variant: 'destructive',
        });
      }
    },
    [addToCart, toast, t]
  );

  useBarcodeScanner({
    onScan: handleScan,
    enabled: true, // 105-8a: always-on amíg az equipment panel látszik
    minLength: 5,
    maxGap: 50,
  });

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handleToggle = (equipment: Equipment) => {
    if (isInCart(equipment.id)) {
      const item = getCartItem(equipment.id);
      if (item) removeFromCart(item.id);
    } else {
      addToCart(equipment);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('rental.wizard.selectEquipmentTitle')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-[#ccfbf1]">
          {t('table.partner')}:{' '}
          <span className="font-medium text-gray-700 dark:text-[#ccfbf1]">{partner?.name}</span> —{' '}
          {t('rental.wizard.selectEquipmentDesc')}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative max-w-xs">
          <Input
            placeholder={t('rental.wizard.searchEquipmentPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={t('common.clearSearch')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant={categoryFilter === 'ALL' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCategoryFilter('ALL')}
          >
            {t('rental.wizard.allFilter', { count: allEquipmentCount })}
          </Button>
          {Object.values(EquipmentCategory).map(cat => (
            <Button
              key={cat}
              variant={categoryFilter === cat ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCategoryFilter(cat)}
            >
              {t(CATEGORY_LABEL_KEYS[cat] ?? cat)}
            </Button>
          ))}
          {/* 105-8a: Scanner aktív jelzés (Sally UX policy: always-on visual cue) */}
          <span
            className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300"
            title={t('rental.wizard.scannerActiveHint')}
            aria-live="polite"
          >
            <span className="h-2 w-2 animate-pulse rounded-full bg-green-500" aria-hidden="true" />
            {t('rental.wizard.scannerActive')}
          </span>
        </div>
      </div>

      {/* Cart summary bar */}
      {cart.length > 0 && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">
                {totalItems}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {t('rental.wizard.cart', { items: cart.length, total: totalItems })}
                </p>
                <p className="text-xs text-gray-500 dark:text-[#ccfbf1]">
                  {t('rental.wizard.dailyTotal', { amount: formatCurrency(getCartTotal()) })}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                for (const item of [...cart]) {
                  removeFromCart(item.id);
                }
              }}
              className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300"
            >
              {t('rental.wizard.clearCart')}
            </Button>
          </div>

          {/* Cart items list — 105-8a: newest-first (reverse render order) */}
          <div className="mt-3 space-y-2">
            {[...cart].reverse().map(item => (
              <div
                key={item.id}
                className="flex items-center justify-between rounded-md bg-white dark:bg-[#1a4f5c] px-3 py-2 shadow-sm"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                    {item.equipment.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-[#ccfbf1]">
                    {item.equipment.serialNumber} — {formatCurrency(item.equipment.dailyRate)}
                    {t('rental.wizard.perDay')}
                  </p>
                </div>

                {/* Quantity controls */}
                <div className="mx-3 flex items-center gap-1">
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      if (item.quantity <= 1) {
                        removeFromCart(item.id);
                      } else {
                        updateQuantity(item.id, item.quantity - 1);
                      }
                    }}
                    className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-300 dark:border-[#2d7d8a] text-gray-500 dark:text-[#ccfbf1] hover:bg-gray-100 dark:hover:bg-[#0f3d47]"
                    title={
                      item.quantity <= 1
                        ? t('rental.wizard.removeItem')
                        : t('rental.wizard.decrease')
                    }
                  >
                    {item.quantity <= 1 ? (
                      <svg
                        className="h-3.5 w-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="h-3.5 w-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 12H4"
                        />
                      </svg>
                    )}
                  </button>
                  <span className="w-8 text-center text-sm font-semibold text-gray-900 dark:text-white">
                    {item.quantity}
                  </span>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      updateQuantity(item.id, item.quantity + 1);
                    }}
                    className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-300 dark:border-[#2d7d8a] text-gray-500 dark:text-[#ccfbf1] hover:bg-gray-100 dark:hover:bg-[#0f3d47]"
                    title={t('rental.wizard.increase')}
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                  </button>
                </div>

                {/* Item subtotal */}
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(item.equipment.dailyRate * item.quantity)}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-[#99f6e4]">
                    {t('rental.wizard.perDay')}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Deposit total */}
          <div className="mt-3 flex justify-between border-t border-primary/20 pt-2 text-sm">
            <span className="text-gray-600 dark:text-[#ccfbf1]">
              {t('rental.wizard.totalDeposit')}
            </span>
            <span className="font-semibold text-amber-600">
              {formatCurrency(
                cart.reduce((sum, item) => sum + item.equipment.depositAmount * item.quantity, 0)
              )}
            </span>
          </div>
        </div>
      )}

      {/* K3 + Csomag-Bérgép Link: Accessory suggestion panel — shown when cart has items.
          Passes the first equipment's ID to the unified /packages/for-equipment endpoint
          which returns BOTH EQUIPMENT-mode (explicit product link) and CATEGORY-mode packages. */}
      {cart.length > 0 && (
        <AccessorySuggestionPanel selectedEquipmentIds={cart.map(item => item.equipment.id)} />
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="py-8 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" />
          <p className="mt-2 text-sm text-gray-500 dark:text-[#ccfbf1]">
            {t('rental.wizard.equipmentLoading')}
          </p>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
          <p className="text-sm text-red-800 dark:text-red-300">
            {t('rental.wizard.equipmentLoadError', { error })}
          </p>
        </div>
      )}

      {/* Equipment grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filteredEquipment.map(equipment => {
          const inCart = isInCart(equipment.id);
          const cartItem = getCartItem(equipment.id);
          return (
            <EquipmentCard
              key={equipment.id}
              equipment={equipment}
              isSelected={inCart}
              quantity={cartItem?.quantity ?? 0}
              onToggle={() => handleToggle(equipment)}
              onAdd={e => {
                e.stopPropagation();
                addToCart(equipment);
              }}
              selectedAccessoryIds={getAccessoriesForEquipment(equipment.id)}
              onToggleAccessory={(accessoryId: string) =>
                toggleAccessory(equipment.id, accessoryId)
              }
            />
          );
        })}
      </div>

      {filteredEquipment.length === 0 && (
        <div className="rounded-lg border-2 border-dashed border-gray-200 dark:border-[#2d7d8a] p-8 text-center">
          <p className="text-gray-500 dark:text-[#ccfbf1]">
            {t('rental.wizard.noEquipmentResults')}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * 105-8a AC-6: Inline suggested accessories using 105-8b frequency endpoint.
 * Shows top-8 suggestions; "Több tartozék ▾" button opens AccessoryDrawer.
 */
function SuggestedAccessoryChips({
  equipmentId,
  equipmentName,
  selectedAccessoryIds,
  onToggleAccessory,
}: {
  equipmentId: string;
  equipmentName: string;
  selectedAccessoryIds: string[];
  onToggleAccessory: (accessoryId: string) => void;
}) {
  const { suggestions, isLoading, error } = useSuggestedAccessories(equipmentId, 8);
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="mt-3 text-xs text-muted-foreground">
        {t('rental.wizard.accessoryDrawer.loadingAccessories')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-3 text-xs text-destructive" role="alert">
        {error}
      </div>
    );
  }

  const totalValue = suggestions
    .filter(a => selectedAccessoryIds.includes(a.id))
    .reduce((sum, a) => sum + (a.value ?? 0), 0);

  return (
    <div className="mt-3 space-y-2">
      {suggestions.length > 0 && (
        <>
          <p className="text-xs font-medium text-muted-foreground">
            {t('rental.wizard.accessoriesLabel')}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map(acc => {
              const isSelected = selectedAccessoryIds.includes(acc.id);
              return (
                <button
                  key={acc.id}
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    onToggleAccessory(acc.id);
                  }}
                  title={acc.description ?? acc.name}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                    acc.isRequired
                      ? isSelected
                        ? 'bg-blue-500 text-white'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50'
                      : isSelected
                        ? 'bg-gray-600 text-white dark:bg-[#0f3d47] dark:text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-[#1f5f6b] dark:text-[#99f6e4] dark:hover:bg-[#0f3d47]'
                  )}
                >
                  {acc.name}
                  {acc.isRequired && (
                    <span className="text-[10px] opacity-75">
                      {t('rental.wizard.accessoryDrawer.isRequired')}
                    </span>
                  )}
                  {acc.value != null && acc.value > 0 && (
                    <span className="text-[10px] opacity-75">{formatCurrency(acc.value)}</span>
                  )}
                </button>
              );
            })}
          </div>
          {totalValue > 0 && (
            <p className="text-xs text-muted-foreground">
              {t('rental.wizard.accessoryValue')}{' '}
              <span className="font-medium">{formatCurrency(totalValue)}</span>
            </p>
          )}
        </>
      )}

      {/* AC-7: "Több tartozék ▾" → AccessoryDrawer Sheet.
          Only shown when the equipment has at least one suggested accessory — otherwise
          the drawer would open into an empty state with no way for the user to add anything. */}
      {suggestions.length > 0 && (
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            setDrawerOpen(true);
          }}
          className="mt-1 inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10"
        >
          {t('rental.wizard.accessoryDrawer.moreAccessoriesBtn')}
          <span aria-hidden="true">▾</span>
        </button>
      )}

      <AccessoryDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        equipmentId={equipmentId}
        equipmentName={equipmentName}
        selectedAccessoryIds={selectedAccessoryIds}
        onToggleAccessory={onToggleAccessory}
      />
    </div>
  );
}

function EquipmentCard({
  equipment,
  isSelected,
  quantity,
  onToggle,
  onAdd,
  selectedAccessoryIds,
  onToggleAccessory,
}: {
  equipment: Equipment;
  isSelected: boolean;
  quantity: number;
  onToggle: () => void;
  onAdd: (e: React.MouseEvent) => void;
  selectedAccessoryIds: string[];
  onToggleAccessory: (accessoryId: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <Card
      className={cn(
        'cursor-pointer transition-all hover:shadow-md',
        isSelected ? 'ring-2 ring-primary shadow-lg' : ''
      )}
      style={
        isSelected
          ? {
              background: 'var(--card-bg-selected)',
              border: '2px solid hsl(var(--primary))',
            }
          : undefined
      }
      onClick={onToggle}
    >
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                EQUIPMENT_STATUS_COLORS[equipment.status] ??
                  'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400'
              )}
            >
              {t(STATUS_LABEL_KEYS[equipment.status] ?? equipment.status)}
            </span>
            <h3 className="mt-2 font-medium text-gray-900 dark:text-white line-clamp-2">
              {equipment.name}
            </h3>
          </div>
          <div
            className={cn(
              'ml-2 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors',
              isSelected
                ? 'border-primary bg-primary text-white'
                : 'border-gray-300 dark:border-[#2d7d8a] bg-white dark:bg-[#1a4f5c]'
            )}
          >
            {isSelected && (
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="mt-3 space-y-1 text-sm text-gray-600 dark:text-[#ccfbf1]">
          {equipment.equipmentCode && (
            <p>
              <span className="text-gray-400 dark:text-[#99f6e4]">
                {t('rental.wizard.itemCode')}
              </span>{' '}
              <span className="font-medium text-gray-800 dark:text-white">
                {equipment.equipmentCode}
              </span>
            </p>
          )}
          <p>
            <span className="text-gray-400 dark:text-[#99f6e4]">SN:</span> {equipment.serialNumber}
          </p>
          {equipment.brand && (
            <p>
              <span className="text-gray-400 dark:text-[#99f6e4]">{t('rental.wizard.brand')}</span>{' '}
              {equipment.brand} {equipment.model}
            </p>
          )}
          <p>
            <span className="text-gray-400 dark:text-[#99f6e4]">
              {t('rental.wizard.condition')}
            </span>{' '}
            {t(CONDITION_LABEL_KEYS[equipment.condition] ?? equipment.condition)}
          </p>
          <p>
            <span className="text-gray-400 dark:text-[#99f6e4]">
              {t('rental.wizard.categoryLabel')}
            </span>{' '}
            {t(CATEGORY_LABEL_KEYS[equipment.category] ?? equipment.category)}
          </p>
        </div>

        {/* Pricing */}
        <div className="mt-4 rounded-lg bg-gray-50 dark:bg-[#1f5f6b] p-3">
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div>
              <p className="text-gray-500 dark:text-[#ccfbf1]">{t('rental.wizard.dailyPrice')}</p>
              <p className="font-semibold text-gray-900 dark:text-white">
                {formatCurrency(equipment.dailyRate)}
              </p>
            </div>
            <div>
              <p className="text-gray-500 dark:text-[#ccfbf1]">{t('rental.wizard.weeklyPrice')}</p>
              <p className="font-semibold text-gray-900 dark:text-white">
                {formatCurrency(equipment.weeklyRate)}
              </p>
            </div>
            <div>
              <p className="text-gray-500 dark:text-[#ccfbf1]">{t('rental.wizard.monthlyPrice')}</p>
              <p className="font-semibold text-gray-900 dark:text-white">
                {formatCurrency(equipment.monthlyRate)}
              </p>
            </div>
          </div>
          <div className="mt-2 border-t border-gray-200 dark:border-[#2d7d8a] pt-2 text-center">
            <p className="text-xs text-gray-500 dark:text-[#ccfbf1]">{t('rental.deposit')}</p>
            <p className="font-semibold text-amber-600">
              {formatCurrency(equipment.depositAmount)}
            </p>
          </div>
        </div>

        {/* Quick add more button when already in cart */}
        {isSelected && (
          <div className="mt-3 flex items-center justify-between rounded-md bg-primary/10 px-3 py-1.5">
            <span className="text-xs font-medium text-primary">
              {t('rental.wizard.inCart', { count: quantity })}
            </span>
            <button
              onClick={onAdd}
              className="rounded-md bg-primary px-2 py-0.5 text-xs font-medium text-white hover:bg-primary/90"
            >
              {t('rental.wizard.addOne')}
            </button>
          </div>
        )}

        {/* AC-6 + AC-7: Inline suggested accessories + "Több tartozék" drawer */}
        {isSelected && (
          <SuggestedAccessoryChips
            equipmentId={equipment.id}
            equipmentName={equipment.name}
            selectedAccessoryIds={selectedAccessoryIds}
            onToggleAccessory={onToggleAccessory}
          />
        )}
      </CardContent>
    </Card>
  );
}
